import React, { useState } from "react";

const ProjectCard = ({ projet, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getCategoryColor = (category) => {
    switch (category) {
      case "Web Application":
        return "bg-gradient-to-r from-blue-500 to-blue-600 text-white";
      case "Mobile App":
        return "bg-gradient-to-r from-green-500 to-green-600 text-white";
      case "E-commerce":
        return "bg-gradient-to-r from-purple-500 to-purple-600 text-white";
      case "Portfolio":
        return "bg-gradient-to-r from-orange-500 to-orange-600 text-white";
      default:
        return "bg-gradient-to-r from-gray-500 to-gray-600 text-white";
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "Web Application":
        return "🌐";
      case "Mobile App":
        return "📱";
      case "E-commerce":
        return "🛒";
      case "Portfolio":
        return "💼";
      default:
        return "🔧";
    }
  };

  return (
    <div
      className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100 transition-all duration-300 flex flex-col"
      style={{ minHeight: 540, height: "100%" }}
    >
      {/* Header avec gradient et badge */}
      <div className="relative p-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center mb-2">
              <span className="text-2xl mr-2">
                {getCategoryIcon(projet.category)}
              </span>
              <h3 className="text-xl font-bold text-gray-900 transition-colors duration-300 truncate leading-tight">
                {projet.name}
              </h3>
            </div>
            <div
              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getCategoryColor(
                projet.category
              )}`}
            >
              {projet.category}
            </div>
          </div>

          {projet.highlight && (
            <div className="absolute top-4 right-4">
              <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse shadow-lg">
                <div className="absolute inset-0 rounded-full bg-yellow-400 opacity-30 animate-ping"></div>
              </div>
            </div>
          )}
        </div>

        <p className="text-sm text-gray-500 mb-2">Créé en {projet.creation}</p>
      </div>

      {/* Image projet avec ratio adapté et object-contain, sans mockup ni bordure */}
      {projet.image ? (
        <div className="relative w-full flex justify-center items-center bg-[#f3f4f6] py-4">
          <div
            className={
              projet.category === "Mobile App"
                ? "w-24 h-48 sm:w-28 sm:h-56 flex items-center justify-center"
                : "w-full max-w-lg flex items-center justify-center"
            }
            style={
              projet.category === "Mobile App"
                ? { height: "192px", maxHeight: "192px" } // 48*4px
                : { aspectRatio: "4/3", height: "192px", maxHeight: "192px" }
            }
          >
            <img
              src={`${process.env.PUBLIC_URL}/Projets/${projet.image}`}
              alt={projet.name}
              className="w-full h-full object-contain"
              style={{
                background: "#f3f4f6",
                maxHeight: "192px",
                height: "100%",
              }}
            />
          </div>
        </div>
      ) : (
        <div className="relative h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center group/image">
          <div className="text-center text-gray-400">
            <span className="text-4xl">{getCategoryIcon(projet.category)}</span>
            <p className="text-sm mt-2 font-medium">Projet en cours</p>
          </div>
        </div>
      )}

      {/* Contenu */}
      <div className="p-6">
        <div className="mb-4">
          <p
            className={`text-gray-700 leading-relaxed transition-all duration-300 ${
              isExpanded ? "" : "line-clamp-3"
            }`}
          >
            {projet.description}
          </p>

            {projet.description.length > 150 && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-primary hover:text-blue-600 text-sm font-medium mt-2 flex items-center group/btn transition-colors duration-200 ui-btn"
            >
              {isExpanded ? "Voir moins" : "Voir plus"}
              <svg
                className={`w-4 h-4 ml-1 transition-transform duration-200 ${
                  isExpanded ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          )}
        </div>

        <div className="mb-6">
          <h4 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider">
            Technologies
          </h4>
          <div className="flex flex-wrap gap-2">
            {projet.technos.map((tech, techIndex) => (
              <span
                key={techIndex}
                className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-800 border border-blue-200 cursor-default"
                style={{ animationDelay: `${techIndex * 100}ms` }}
              >
                {tech}
              </span>
            ))}
          </div>
        </div>

        {/* Actions et informations */}
        <div className="space-y-3">
          {/* Liens disponibles */}
          <div className="flex space-x-3">
              {projet.github && (
              <a
                href={projet.github}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-gray-900 text-white px-4 py-2.5 rounded-md text-sm font-medium transition-all duration-200 flex items-center justify-center hover:shadow-md hover:-translate-y-0.5 transform focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ui-btn"
              >
                <svg
                  className="w-4 h-4 mr-2 group-hover/link:scale-110 transition-transform duration-200"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                </svg>
                GitHub
              </a>
            )}
            {projet.demo && (
              <a
                href={projet.demo}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-gradient-to-r from-primary to-blue-600 text-white px-4 py-2.5 rounded-md text-sm font-medium transition-all duration-200 flex items-center justify-center hover:shadow-md hover:-translate-y-0.5 transform focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ui-btn"
              >
                <svg
                  className="w-4 h-4 mr-2 group-hover/link:scale-110 transition-transform duration-200"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
                Démo
              </a>
            )}
            {!projet.github && !projet.demo && (
              <div className="flex-1 bg-gray-100 text-gray-600 px-4 py-2.5 rounded-md text-sm font-medium flex items-center justify-center">
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Projet privé
              </div>
            )}
          </div>

          {projet.type && (
            <div className="text-center">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-2"></span>
                {projet.type}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-primary/3 to-blue-600/3 rounded-full -mr-8 -mt-8 transition-transform duration-700 opacity-40"></div>
    </div>
  );
};

export default ProjectCard;
